import {useState, useEffect} from "react";
import {Settings, MapPin, Plus, X, Check, CheckCircle} from "lucide-react";
import {useNavigate, useLocation} from "react-router-dom";
import Header from "../components/Header";
import {getConfigHandle, saveConfigHandle} from "../services/configService";
import {useAppConfig} from "../context/AppConfigContext";

const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === "") {
        return {isValid: false, message: `${fieldName} is required`};
    }
    return {isValid: true, message: ""};
};

export default function Home() {
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const {state} = useLocation();
    const { appConfig, setAppConfig } = useAppConfig();

    const [config, setConfig] = useState({
        storeHash: state?.storeHash || "",
        fileType: state?.fileType || "",
        fileUrl: state?.fileUrl || "",
    });
    const [configErrors, setConfigErrors] = useState({});

    const handleConfigChange = (field, value) => {
        setConfig({...config, [field]: value});
        const validation = validateRequired(value, field);
        setConfigErrors({...configErrors, [field]: validation.isValid ? "" : validation.message});
    };

    const handleSaveConfig = async () => {
        const requiredFields = ["fileType", "fileUrl"];
        const errors = {};
        requiredFields.forEach(field => {
            const validation = validateRequired(config[field], field);
            if (!validation.isValid) errors[field] = validation.message;
        });
        if (Object.keys(errors).length > 0) {
            setConfigErrors(errors);
            return;
        }

        setIsLoading(true);
        try {
            const res = await saveConfigHandle(config);
            console.log("Save config response:", res);
            if (!res) {
                setMessage("Failed to save configuration");
                return;
            }
            setMessage("App configuration saved successfully!");
            const contextData = {storeHash: config.storeHash, savedConfig: res};
            setAppConfig(contextData);
            setTimeout(() => {
                navigate("/auth/load", {state:{storeHash: config.storeHash}});
                setMessage("");
            }, 1500);
        } catch {
            setMessage("Network error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const isConfigFormValid = !Object.values(configErrors).some(Boolean) &&
        config.fileType && config.fileUrl;

    return (
        <div className="min-h-screen bg-black">
            <Header/>
            <main className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome</h1>
                    <p className="text-gray-400">Complete your app setup to get started</p>
                </div>
                <div className="mb-8">
                    <div className="flex items-center space-x-4">
                        <div
                            className={"flex items-center text-green-400"}>
                             <CheckCircle size={20}/>
                            <span className="ml-2 text-sm font-medium">App Configuration</span>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-900 rounded-lg border border-gray-800">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-white mb-6">Step 1: Configure Your App</h2>
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[
                                        // {label: "Store Hash", field: "storeHash"},
                                        {label: "File URL", field: "fileUrl"},
                                    ].map(({label, field}) => (
                                        <div key={field}>
                                            <label
                                                className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
                                            <input
                                                type="text"
                                                placeholder={`Enter ${label}`}
                                                value={config[field]}
                                                onChange={(e) => handleConfigChange(field, e.target.value)}
                                                className={`w-full px-4 py-3 bg-gray-800 border ${configErrors[field] ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                            />
                                            {configErrors[field] && (
                                                <p className="mt-2 text-sm text-red-400">{configErrors[field]}</p>
                                            )}
                                        </div>
                                    ))}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">File
                                            Type</label>
                                        <select
                                            value={config.fileType}
                                            onChange={(e) => handleConfigChange("fileType", e.target.value)}
                                            className={`w-full px-4 py-3 bg-gray-800 border ${configErrors.fileType ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                        >
                                            <option value="">Select File Type</option>
                                            {['csv', 'xlsx'].map(type => (
                                                <option key={type} value={type}>{type.toUpperCase()}</option>
                                            ))}
                                        </select>
                                        {configErrors.fileType &&
                                            <p className="mt-2 text-sm text-red-400">{configErrors.fileType}</p>}
                                    </div>
                                </div>
                                <button
                                    onClick={handleSaveConfig}
                                    disabled={!isConfigFormValid || isLoading}
                                    className="w-full md:w-auto px-6 py-3 bg-[#c4fa00] hover:bg-[#e0ff80] disabled:bg-gray-600 text-black font-semibold rounded-lg"
                                >
                                    {isLoading ? "Saving..." : "Save & Continue"}
                                </button>
                            </div>
                        </div>
                    {message && (
                        <div className="px-6 pb-6">
                            <p className={`text-sm ${message.includes("successfully") ? "text-green-400" : "text-red-400"}`}>
                                {message}
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
